import { ConversationBuilderLocale } from "@/locales/localeid";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import {
	DefaultIsConclusionDialogVal,
	DialogDefaultInteractionStyle,
	PathDefaultInteractionStyle,
	StartDialogDefaultInteractionStyle
} from "@/util/constants";
import { BuilderPageType, InteractionStyles } from "@/util/enum";
import { copyToClipboard, mergeClasses, readFromClipboard } from "@/util/func";
import ConversationActions from "@/views/conversation-builder/components/conversation-actions/conversation-actions.vue";
import { countObjects, EntityLengths } from "@/views/conversation-builder/conversation-builder-util";
import {
	Connector,
	Conversation,
	ConversationNode,
	ConversationNodeType,
	Dialog,
	GroundConfig,
	INodes,
	Path,
	NodeData,
	EntityDataSet,
	IMetaData
} from "@/views/conversation-builder/conversation-builder.model";
import { Entity } from "@/views/conversation-builder/models/entity.model";
import { Intent } from "@/views/conversation-builder/models/intent.model";
import { fmtMsg } from "commonui";
import * as d3 from "d3";
import { zoomIdentity } from "d3";
import elementResizeEvent from "element-resize-event";
import { cloneDeep, debounce, max } from "lodash";
import { v4 } from "uuid";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import {
	connectorPathGenerator,
	ConversationConfig,
	DialogConfig,
	ellipsisNodeTitle,
	PathConfig,
	intersects,
	IRendererActions,
	isControlKey,
	nodeConfig,
	nodeIdAttr,
	nodeKeyAttr,
	nodeTransformAttr,
	SVGStateHandler,
	isControlandAltKey
} from "./conversation-renderer-util";

@Component({
	components: {
		ConversationActions
	}
})
export default class ConversationRenderer extends Vue {
	@Prop() selectedNode!: string | null;
	@Prop() addNode!: <T = NodeData>(node: ConversationNode<T>) => void;
	@Prop() connectorPreValidation!: (nodeId: string) => string | null;
	@Prop() connectorPostValidation!: (nodeId: string, targetNodeId: string) => string | null;
	@Prop() addNodeConnector!: (nodeId: string, targetNodeId: string, connectorId: string) => void;
	@Prop() addNodeConnectorBetweenNodes!: (nodeId: string, targetNodeId: string, connectorId: string) => void;
	@Prop() getConnectors!: (nodeId: string, source?: boolean) => ConversationNode<Connector>[];
	@Prop({ default: () => [] }) extraActions!: IRendererActions[];
	@Prop({ default: false }) readonly!: boolean;
	@Prop({ default: false }) showDraftCompleteAction!: boolean;

	private groundConfig: GroundConfig = {
		minX: 0,
		minY: 0,
		maxX: 0,
		maxY: 0,
		element: undefined!,
		width: 0,
		height: 0,
		minimapWidth: 0
	};
	private groundZoomBehaviour!: d3.ZoomBehavior<Element, unknown>;
	private groundZoomEventForced: boolean = false;
	private groundZoomTransform: { k: number; x: number; y: number } = { k: 1, x: 0, y: 0 };
	private groundContainerRect!: DOMRect;
	private minimapBrushBehaviour!: d3.BrushBehavior<unknown>;
	private minimapBrushGroup!: d3.Selection<SVGGElement, any, any, any>;

	private builderSelection!: d3.Selection<HTMLElement, HTMLDivElement, HTMLElement, any>;
	private svg!: d3.Selection<SVGSVGElement, any, HTMLElement, any>;
	private svgGroup!: d3.Selection<SVGGElement, any, any, any>;
	private svgMinimap!: d3.Selection<SVGElement, any, HTMLElement, any>;

	private svgStateHandler!: SVGStateHandler;

	private nodeDragBehaviour: d3.DragBehavior<SVGGElement, any, any> | undefined;
	private connectorDragBehaviour: d3.DragBehavior<SVGCircleElement, any, any> | undefined;
	private conversationStore = getModule(ConversationBuilderState);

	$refs!: {
		tooltipContainer: HTMLDivElement;
	};

	mounted() {
		this.setGroundConfig();
		this.enterGround();
		if (!this.readonly) {
			this.setToolboxDragDrop();
			this.setNodesDragBehaviour();
			this.setConnectorDragBehaviour();
			this.setAreaSelection();
		}

		// Update ground on resize
		elementResizeEvent(
			this.groundConfig.element,
			debounce(() => {
				this.groundZoomEventForced = true;
				this.setGroundConfig();
				this.updateMinimap();
				this.minimapBrushBehaviour.move(this.minimapBrushGroup, [
					[0, 0],
					[this.groundConfig.width, this.groundConfig.height]
				]);
				this.transform(0, 0, 1);
			}, 500)
		);

		this.updateSVG();
		this.keyboardEventInitialize();
	}

	beforeDestroy() {
		this.keyboardEventDestroy();
		this.exitGround();
	}

	@Watch("conversationStore.nodes", { deep: true })
	@Watch("conversationStore.selectedNodes", { deep: true })
	@Watch("selectedNode")
	public updateSVG() {
		const nodesArray = Object.values(this.conversationStore.nodes);

		// Connetors
		this.updateSVGConnector(nodesArray);

		// Conversation
		this.updateSVGConversation(nodesArray);

		// Dialog
		this.updateSVGDialog(nodesArray);

		// Paths
		this.updateSVGPath(nodesArray);

		// External Conversation
		this.updateExternalConversation(nodesArray);

		// Update minimap size
		this.updateMinimap();

		// Paint minimap
		this.paintMinimap();
	}

	private enterGround() {
		const { width, height, minimapWidth } = this.groundConfig;
		const self = this;
		const minmapViewboxSize = Math.max(width, height);

		this.builderSelection = d3.select<HTMLElement, HTMLDivElement>("#builder-area");

		this.svg = this.builderSelection
			.append("svg")
			.attr("class", "cb-ground")
			.attr("width", "100%")
			.attr("height", "100%")
			.attr("isBackground", true);
		this.svg.on("click", this.backgroundClicked);
		this.svgGroup = this.svg.append("g").attr("class", "cb-ground__content");
		this.svgStateHandler = new SVGStateHandler(this.svg);

		const minimapScaleX = (width: number, scale: number) => d3.scaleLinear([0, width], [0, width * scale]);
		const minimapScaleY = (height: number, scale: number) => d3.scaleLinear([0, height], [0, height * scale]);

		this.svgMinimap = d3
			.select("#minimap")
			.append<SVGElement>("svg")
			.attr("width", minimapWidth)
			.attr("height", minimapWidth)
			.attr("viewBox", [0, 0, minmapViewboxSize, minmapViewboxSize].join(" "))
			.style("vertical-align", "top")
			.attr("preserveAspectRatio", "xMidYMid meet");

		function onZoom(event: d3.D3ZoomEvent<any, any>) {
			if (!event.sourceEvent && !self.groundZoomEventForced) {
				// prevent brush invoked event
				return null;
			}

			const { width, height } = self.groundConfig;

			// Updating ground transform
			self.groundZoomTransform = event.transform;
			self.svgGroup.attr("transform", self.groundZoomTransform as any);

			// Updating brush transform
			const { k, x, y } = self.groundZoomTransform;
			const scaleX = minimapScaleX(width, k);
			const scaleY = minimapScaleY(height, k);
			self.minimapBrushBehaviour.move(self.minimapBrushGroup, [
				[scaleX.invert(-x), scaleY.invert(-y)],
				[scaleX.invert(-x + width), scaleY.invert(-y + height)]
			]);
			self.groundZoomEventForced = false;
		}

		this.groundZoomBehaviour = d3
			.zoom()
			.scaleExtent([0.2, 4])
			.translateExtent([
				[0, 0],
				[width, height]
			])
			.on("zoom", onZoom);

		this.minimapBrushGroup = this.svgMinimap.append("g");

		function onBrush(event: d3.D3BrushEvent<any>) {
			if (event && !event.sourceEvent) {
				// prevent zoom invoked event
				return null;
			}
			if (!Array.isArray(event.selection)) {
				// if selection does not exists
				return null;
			}

			const { width, height } = self.groundConfig;

			// Processing brush and zoom
			const [[brushX, brushY]] = event.selection as [[number, number], [number, number]];
			const scale = d3.zoomTransform(self.svg.node()!).k;
			const x = minimapScaleX(width, scale)(-brushX);
			const y = minimapScaleY(height, scale)(-brushY);
			self.transform(x, y, scale);
		}

		this.minimapBrushBehaviour = d3
			.brush()
			.extent([
				[0, 0],
				[width, height]
			])
			.on("brush", onBrush);

		this.svg.call(this.groundZoomBehaviour as any, zoomIdentity);
		this.minimapBrushGroup.call(this.minimapBrushBehaviour);

		this.minimapBrushBehaviour.move(this.minimapBrushGroup, [
			[0, 0],
			[width, height]
		]);

		this.svgMinimap.selectAll(".handle").remove();
		this.svgMinimap.selectAll(".overlay").remove();
	}

	private exitGround() {
		this.svg.remove();
		this.svgMinimap.remove();
	}

	private backgroundClicked(e: MouseEvent & { path: any[] }) {
		if (this.readonly) {
			return;
		}
		if (isControlKey(e)) {
			return;
		}
		const targetElement = e.target as SVGElement;
		if (targetElement?.getAttribute("isBackground") === "true") {
			this.selectNode(null!, { id: null } as any);
			e.preventDefault();
			e.cancelBubble = true;
			e.stopPropagation();
		}
	}

	public tooltipContainer() {
		return this.$refs.tooltipContainer;
	}

	private updateSVGConversation(nodes: ConversationNode[]): void {
		const conversations = nodes.filter(
			(node) => node.type === ConversationNodeType.Conversation
		) as ConversationNode<Dialog>[];
		const conversationSelection = this.svgGroup.selectAll("g[START]").data(conversations);
		const getConversationClass = (node: ConversationNode<Dialog>): string => {
			const isSelected = this.conversationStore.selectedNodes[node.id];
			return mergeClasses("cb-node", "cb-conversation", isSelected && "cb-node--selected");
		};

		// Update
		conversationSelection
			.attr("transform", nodeTransformAttr)
			.attr("class", getConversationClass)
			.attr("id", nodeIdAttr)
			.attr("key", nodeKeyAttr)
			.attr("START", nodeIdAttr);
		conversationSelection.select("text").text((node) => ellipsisNodeTitle(node));

		// Create
		const conversationEnterSelection = conversationSelection
			.enter()
			.append("g")
			.attr("START", nodeIdAttr)
			.attr("class", getConversationClass)
			.attr("key", nodeKeyAttr)
			.attr("id", nodeIdAttr)
			.attr("transform", nodeTransformAttr);

		if (!this.readonly) {
			conversationEnterSelection.on("click", this.selectNode).call(this.nodeDragBehaviour as any);
		}

		conversationEnterSelection.append("circle").attr("r", 30).attr("cx", "31").attr("cy", "31");
		conversationEnterSelection
			.append("text")
			.text((node) => ellipsisNodeTitle(node))
			.classed("cb-node__label", true)
			.attr("x", ConversationConfig.width / 2)
			.attr("y", ConversationConfig.height / 2 + 3);

		// Remove
		conversationSelection.exit().remove();
	}

	private updateSVGDialog(nodes: ConversationNode[]): void {
		const dialogs = nodes.filter((node) => node.type === ConversationNodeType.Dialog) as ConversationNode<Dialog>[];
		const dialogSelection = this.svgGroup.selectAll("g[DIALOG]").data(dialogs);
		const getDialogClass = (node: ConversationNode<Dialog>): string => {
			const isSelected = this.conversationStore.selectedNodes[node.id];
			return mergeClasses("cb-node", "cb-dialog", isSelected && "cb-node--selected");
		};

		// Update
		dialogSelection
			.attr("transform", nodeTransformAttr)
			.attr("class", getDialogClass)
			.attr("id", nodeIdAttr)
			.attr("key", nodeKeyAttr)
			.attr("DIALOG", nodeIdAttr)
			.attr("transform", nodeTransformAttr);
		dialogSelection.select("text").text((node) => ellipsisNodeTitle(node));

		// Create
		const dialogEnterSelection = dialogSelection
			.enter()
			.append("g")
			.attr("DIALOG", nodeIdAttr)
			.attr("class", getDialogClass)
			.attr("id", nodeIdAttr)
			.attr("transform", nodeTransformAttr);

		if (!this.readonly) {
			dialogEnterSelection.on("click", this.selectNode).call(this.nodeDragBehaviour as any);
		}

		dialogEnterSelection.append("rect").attr("width", DialogConfig.width).attr("height", "50");
		dialogEnterSelection
			.append("text")
			.text((node) => ellipsisNodeTitle(node))
			.classed("cb-node__label", true)
			.attr("x", DialogConfig.width / 2)
			.attr("y", DialogConfig.height / 2 + 3);

		// Remove
		dialogSelection.exit().remove();
	}

	private updateSVGPath(nodes: ConversationNode[]): void {
		const intents = nodes.filter((node) => node.type === ConversationNodeType.Path) as ConversationNode<Path>[];
		const intentSelection = this.svgGroup.selectAll("g[INTENT]").data(intents);

		const getPathClass = (node: ConversationNode<Path>): string => {
			const isSelected = this.conversationStore.selectedNodes[node.id];
			return mergeClasses(
				"cb-node",
				"cb-intent",
				isSelected && "cb-node--selected",
				node.data.isFallback && "cb-intent--fallback"
			);
		};

		// Update
		intentSelection
			.attr("transform", nodeTransformAttr)
			.attr("class", getPathClass)
			.attr("id", nodeIdAttr)
			.attr("key", nodeKeyAttr)
			.attr("INTENT", nodeIdAttr);
		intentSelection.select("text").text((node) => ellipsisNodeTitle(node));

		// Create
		const intentEnterSelection = intentSelection
			.enter()
			.append("g")
			.attr("INTENT", nodeIdAttr)
			.attr("class", getPathClass)
			.attr("id", nodeIdAttr)
			.attr("transform", nodeTransformAttr);

		if (!this.readonly) {
			intentEnterSelection.call(this.nodeDragBehaviour as any).on("click", this.selectNode);
			intentSelection.on("click", this.selectNode);
		}

		intentEnterSelection.append("polygon").attr("points", "70,0 140,50 70,100 0,50");
		intentEnterSelection
			.append("text")
			.text((node) => ellipsisNodeTitle(node))
			.classed("cb-node__label", true)
			.attr("x", PathConfig.width / 2)
			.attr("y", PathConfig.height / 2 + 3);

		// Remove
		intentSelection.exit().remove();
	}

	private updateExternalConversation(nodes: ConversationNode[]): void {
		const conversations = nodes.filter(
			(node) => node.type === ConversationNodeType.ExternalConversation
		) as ConversationNode<Dialog>[];
		const conversationSelection = this.svgGroup.selectAll("g[EXTERNAL-CONVERSATION]").data(conversations);
		const getConversationClass = (node: ConversationNode<Dialog>): string => {
			const isSelected = this.conversationStore.selectedNodes[node.id];
			return mergeClasses("cb-node", "cb-external-conversation", isSelected && "cb-node--selected");
		};

		// Update
		conversationSelection
			.attr("transform", nodeTransformAttr)
			.attr("class", getConversationClass)
			.attr("EXTERNAL-CONVERSATION", nodeIdAttr)
			.attr("id", nodeIdAttr);
		conversationSelection.select("text").text((node) => node.data.name);

		// Create
		const dialogEnterSelection = conversationSelection
			.enter()
			.append("g")
			.attr("EXTERNAL-CONVERSATION", nodeIdAttr)
			.attr("class", getConversationClass)
			.attr("id", nodeIdAttr)
			.attr("transform", nodeTransformAttr);

		if (!this.readonly) {
			dialogEnterSelection.call(this.nodeDragBehaviour as any).on("click", this.selectNode);
		}

		dialogEnterSelection
			.append("rect")
			.attr("width", DialogConfig.width)
			.attr("height", "50")
			.attr("rx", 10)
			.attr("ry", 10);
		dialogEnterSelection
			.append("text")
			.text((node) => node.data.name)
			.classed("cb-node__label", true)
			.attr("x", DialogConfig.width / 2)
			.attr("y", DialogConfig.height / 2 + 3);

		// Remove
		conversationSelection.exit().remove();
	}

	private updateSVGConnector(nodes: ConversationNode[]): void {
		const connectors = nodes.filter((node) => node.type === ConversationNodeType.Connector) as ConversationNode<
			Connector
		>[];
		const connectorData: {
			id: string;
			source: ConversationNode;
			target?: ConversationNode;
		}[] = connectors.map((node) => ({
			id: node.id,
			source: this.conversationStore.nodes[node.data.sourceId],
			target: node.data.targetId ? this.conversationStore.nodes[node.data.targetId] : undefined
		}));

		const connectorSelection = this.svgGroup.selectAll("path[CONNECTOR]").data(connectorData);

		const d = ({ source, target }) => {
			const sourceNodeConfig = nodeConfig[source.type];
			const targetNodeConfig = target ? nodeConfig[target.type] : DialogConfig;

			const targetX = target
				? target.coords.x + targetNodeConfig.width / 2
				: source.coords.x + sourceNodeConfig.width + 100;
			const targetY = target ? target.coords.y : source.coords.y + sourceNodeConfig.height + 100;

			return connectorPathGenerator({
				source: [source.coords.x + sourceNodeConfig.width / 2, source.coords.y + sourceNodeConfig.height],
				target: [targetX, targetY]
			}) as string;
		};
		const getConversationClass = (node): string => {
			const isSelected = this.conversationStore.selectedNodes[node.id];
			return mergeClasses("cb-node", "cb-connector", isSelected && "cb-node--selected");
		};

		// Update
		connectorSelection
			.attr("d", d as any)
			.attr("id", (connector) => connector.id)
			.attr("class", getConversationClass)
			.attr("CONNECTOR", (connector) => connector.id)
			.attr("source-id", (connector) => connector.source.id)
			.attr("target-id", (connector) => connector.target?.id || "");

		// Create
		const enterConnectorSelection = connectorSelection
			.enter()
			.append("path")
			.attr("CONNECTOR", (connector) => connector.id)
			.attr("id", (connector) => connector.id)
			.attr("class", getConversationClass)
			.attr("source-id", (connector) => connector.source.id)
			.attr("target-id", (connector) => connector.target?.id || "")
			.attr("fill", "none")
			.attr("stroke", "black")
			.attr("stroke-width", "3")
			.attr("d", d as any);

		if (!this.readonly) {
			enterConnectorSelection.on("click", (event, n) =>
				this.selectNode(event, this.conversationStore.nodes[n.id])
			);
		}

		// Remove
		connectorSelection.exit().remove();

		// Drag pointer
		this.updateSVGConnectorDragPointer(connectorData);
	}

	private updateSVGConnectorDragPointer(
		connectors: {
			id: string;
			source: ConversationNode;
			target?: ConversationNode;
		}[]
	): void {
		const selection = this.svgGroup
			.selectAll("circle[CONNECTOR_DRAG]")
			.data(connectors.filter((connector) => !connector.target));

		// Update
		selection
			.attr("cx", (connector) => connector.source.coords.x + nodeConfig[connector.source.type].width + 100)
			.attr("cy", (connector) => connector.source.coords.y + nodeConfig[connector.source.type].height + 100)
			.attr("connector-id", (connector) => connector.id);

		// Create
		const enterSelection = selection
			.enter()
			.append("circle")
			.classed("cb-connector__dragger", true)
			.attr("CONNECTOR_DRAG", "")
			.attr("connector-id", (connector) => connector.id)
			.attr("r", 8)
			.attr("cx", (connector) => connector.source.coords.x + nodeConfig[connector.source.type].width + 100)
			.attr("cy", (connector) => connector.source.coords.y + nodeConfig[connector.source.type].height + 100)
			.style("cursor", "pointer")
			.attr("fill", "black");

		if (!this.readonly) {
			enterSelection.call(this.connectorDragBehaviour as any);
		}

		// Remove
		selection.exit().remove();
	}

	private updateMinimap(): void {
		const svgRect = this.svgGroup.node()?.getBBox()!;
		const width = svgRect.width + 300 + svgRect.x;
		const height = svgRect.height + 300 + svgRect.y;
		const minmapViewboxSize = Math.round(
			Math.max(width, height, this.groundContainerRect.width, this.groundContainerRect.height)
		);
		this.svgMinimap.attr("viewBox", `0 0 ${minmapViewboxSize} ${minmapViewboxSize}`);
		this.groundZoomBehaviour.translateExtent([
			[0, 0],
			[minmapViewboxSize, minmapViewboxSize]
		]);
		this.minimapBrushBehaviour.extent([
			[0, 0],
			[minmapViewboxSize, minmapViewboxSize]
		]);
		this.minimapBrushGroup.call(this.minimapBrushBehaviour);
		this.svgMinimap.selectAll(".handle").remove();
		this.svgMinimap.selectAll(".overlay").remove();
		this.svg.call(this.groundZoomBehaviour as any);
	}

	private paintMinimap(): void {
		d3.select("g#minimapPaint").remove();
		document
			.querySelector("#minimap svg > g")!
			.insertAdjacentHTML("beforebegin", `<g id="minimapPaint">${this.svgGroup.html()}</g>`);
	}

	private setToolboxDragDrop(): void {
		const self = this;
		const started = function (this: HTMLElement, event) {
			const node = d3.select(this).clone(true).classed("dragging", true).style("display", "none");
			const dragging = (event, d) => {
				const halfOfWidth = this.clientWidth / 2;
				const halfOfHeight = this.clientHeight / 2;
				const left = event.sourceEvent.x - halfOfWidth;
				const top = event.sourceEvent.y - halfOfHeight;
				node.raise().attr(
					"style",
					`pointer-events: none;display: block; position: fixed; z-index: 9; left: ${left}px; top: ${top}px`
				);

				// highlighing
				self.svgGroup.selectAll(".cb-node--dragging").classed("cb-node--dragging", false);
				const targetElement = document.elementFromPoint(
					event.sourceEvent.clientX - 10,
					event.sourceEvent.clientY
				)! as SVGElement;
				const parentNode = targetElement?.parentNode! as SVGGElement;

				if (
					targetElement &&
					(parentNode.hasAttribute("DIALOG") ||
						parentNode.hasAttribute("INTENT") ||
						parentNode.hasAttribute("START") ||
						parentNode.hasAttribute("EXTERNAL-CONVERSATION"))
				) {
					parentNode.classList.add("cb-node--dragging");
				}
			};

			const ended = (event: any) => {
				const isTouched = self.isTouchGroundBoundry(event.sourceEvent.x, event.sourceEvent.y);
				if (isTouched) {
					const nodeType = parseInt(this.id) as ConversationNodeType;
					const coordinates = self.getTouchedCoordinates(event.sourceEvent.x, event.sourceEvent.y, nodeType);
					switch (nodeType) {
						case ConversationNodeType.Path:
							self.createPath(coordinates.x, coordinates.y);
							break;
						case ConversationNodeType.Connector:
							self.createConnector(event, coordinates.x, coordinates.y);
							break;
						case ConversationNodeType.Dialog:
							self.createDialog(event, coordinates.x, coordinates.y);
							break;
						case ConversationNodeType.Conversation:
							self.createDialog(event, coordinates.x, coordinates.y, true);
							break;
						case ConversationNodeType.ExternalConversation:
							self.createExternalConversation(event, coordinates.x, coordinates.y);
							break;
					}
				}
				node.classed("dragging", false).remove();
				self.svgGroup.selectAll(".cb-node--dragging").classed("cb-node--dragging", false);
			};
			event.on("drag", dragging).on("end", ended);
		};
		const dragBehaviour = d3.drag().on("start", started as any);
		d3.selectAll(".cb-toolbox__item > svg").call(dragBehaviour as any);
	}

	private setGroundConfig(): void {
		const groundElement = document.querySelector<HTMLDivElement>(".conre__wrap")!;
		const minimapElement = document.querySelector("#minimap")!;
		this.groundContainerRect = groundElement.getBoundingClientRect();
		const minimapContainerRect = minimapElement.getBoundingClientRect();
		const { left, top, width, height } = this.groundContainerRect;
		this.groundConfig = {
			minX: left,
			maxX: left + width,
			minY: top,
			maxY: top + height,
			element: groundElement,
			width: width,
			height: height,
			minimapWidth: minimapContainerRect.width
		};
	}

	private setNodesDragBehaviour() {
		const self = this;

		// A check, whether the drag event is occured or not, because start/end event gets fired event on click of element.
		let isDraggingEvent = false;

		// Selected nodes (except connectors since they have to moved with nodes)
		let nodesToDrag: string[] = [];
		let nodesToDragUnordered: { [key: string]: boolean };

		// Group element - while dragging, we move all selected nodes to a group and then move the group elememt around.
		let groupElement: d3.Selection<SVGGElement, any, any, any>;
		let groupElementDOMRect: DOMRect;

		// Capturing start event, containing x/y coordinates, where the dragging starts from.
		let startEvent: d3.D3DragEvent<SVGElement, any, any>;

		// A check to declare whether the drag has been started (after a time threshold of 150ms)
		let dragStarted: boolean = false;
		let dragStartedTimeOut: NodeJS.Timeout | null;

		const getCoordinates = (event: d3.D3DragEvent<SVGGElement, any, any>) => {
			const x = max([self.positiveNumber(event.x) - startEvent.x, groupElementDOMRect.x * -1])!;
			const y = max([self.positiveNumber(event.y) - startEvent.y, groupElementDOMRect.y * -1])!;
			return { x, y };
		};

		this.nodeDragBehaviour = d3
			.drag<SVGGElement, any, any>()
			.on("start", function (this: SVGGElement, event) {
				dragStarted = false;
				dragStartedTimeOut = setTimeout(() => {
					self.svgStateHandler.dragStart();

					const selected = Object.keys(self.conversationStore.selectedNodes);
					nodesToDragUnordered = {};

					if (selected.length > 1 && self.conversationStore.selectedNodes[this.id]) {
						nodesToDrag = Object.keys(self.conversationStore.selectedNodes).filter((nodeId) => {
							if (self.conversationStore.nodes[nodeId].type !== ConversationNodeType.Connector) {
								nodesToDragUnordered[nodeId] = true;
								return true;
							}
							return false;
						});
					} else {
						nodesToDrag = [this.id];
						nodesToDragUnordered[this.id] = true;
					}

					groupElement = self.svgGroup.append("g");
					if (groupElement) {
						nodesToDrag.forEach((nodeId) => {
							groupElement.append(() => self.svgGroup.select(`#${nodeId}`).remove().node());
						});
						groupElementDOMRect = groupElement.node()!.getBBox();
					}
					startEvent = event;
					dragStarted = true;
				}, 150);
			})
			.on("drag", function (this: SVGGElement, event: d3.D3DragEvent<SVGGElement, any, any>) {
				if (!dragStarted) {
					return;
				}
				isDraggingEvent = true;
				const { x, y } = getCoordinates(event);
				groupElement.attr("transform", d3.zoomIdentity.translate(x, y) as any);

				nodesToDrag.forEach((nodeId) => {
					const node = self.conversationStore.nodes[nodeId];
					const nodeDimension = nodeConfig[node.type];
					const nodeX = x + node.coords.x + nodeDimension.width / 2;
					const nodeY = y + node.coords.y + nodeDimension.height;
					self.svgGroup.selectAll<SVGPathElement, any>(`path[source-id="${nodeId}"]`).attr("d", function () {
						const targetNodeId = this.getAttribute("target-id") as string | null;

						// When the connector isn't connected to a target
						let targetNodeX = x + node.coords.x + 100;
						let targetNodeY = y + node.coords.y + 100;

						if (targetNodeId) {
							const targetNode = self.conversationStore.nodes[targetNodeId];
							const targetNodeConfig = nodeConfig[targetNode.type];

							const isTargetNodeSelected = nodesToDragUnordered[targetNodeId];

							// When the connector is connected to a target and target is not selected.
							targetNodeX = targetNode.coords.x + targetNodeConfig.width / 2;
							targetNodeY = targetNode.coords.y;

							if (isTargetNodeSelected) {
								// When the connector is connected to a target and target is selected.
								targetNodeX = x + targetNode.coords.x + targetNodeConfig.width / 2;
								targetNodeY = y + targetNode.coords.y;
							}
						}

						// Moving the connector's dragger with the connector.
						d3.select(`circle[connector-id="${this.getAttribute("id")}"]`)
							.attr("cx", targetNodeX)
							.attr("cy", targetNodeY);

						return connectorPathGenerator({
							source: [nodeX, nodeY],
							target: [targetNodeX, targetNodeY]
						});
					});
					self.svgGroup.selectAll<SVGPathElement, any>(`path[target-id="${nodeId}"]`).attr("d", function () {
						const sourceNodeId = this.getAttribute("source-id") as string;
						const sourceNode = self.conversationStore.nodes[sourceNodeId];
						const sourceNodeConfig = nodeConfig[sourceNode.type];

						const isSourceNodeSelected = nodesToDragUnordered[sourceNodeId];

						let sourceNodeX = sourceNode.coords.x + sourceNodeConfig.width / 2;
						let sourceNodeY = sourceNode.coords.y + sourceNodeConfig.height;

						if (isSourceNodeSelected) {
							sourceNodeX = x + sourceNode.coords.x + sourceNodeConfig.width / 2;
							sourceNodeY = y + sourceNode.coords.y + sourceNodeConfig.height;
						}

						return connectorPathGenerator({
							source: [sourceNodeX, sourceNodeY],
							target: [nodeX, nodeY - nodeDimension.height]
						});
					});
				});
				self.svgStateHandler.dragging();
			})
			.on("end", function (this: SVGGElement, event: d3.D3DragEvent<SVGGElement, any, any>) {
				if (dragStartedTimeOut) {
					clearTimeout(dragStartedTimeOut);
					dragStartedTimeOut = null;
				}
				if (isDraggingEvent) {
					const { x, y } = getCoordinates(event);

					const update = nodesToDrag.map((nodeId) => {
						return {
							id: nodeId,
							x: x + self.conversationStore.nodes[nodeId].coords.x,
							y: y + self.conversationStore.nodes[nodeId].coords.y
						};
					});
					self.conversationStore.updateCoords(update);
				}
				if (groupElement) {
					groupElement.selectAll<SVGElement, any>("g").each(function () {
						self.svgGroup.append(() => d3.select(this).remove().node());
					});
					groupElement.remove();
				}
				isDraggingEvent = false;
				self.svgStateHandler.dragEnd();
			} as any);
	}

	private setConnectorDragBehaviour() {
		const self = this;

		const getTargateNode = (event: d3.D3DragEvent<SVGGElement, any, any>) => {
			const dropElement = event.sourceEvent.target;
			if (
				dropElement &&
				(dropElement.parentNode.hasAttribute("DIALOG") ||
					dropElement.parentNode.hasAttribute("INTENT") ||
					dropElement.parentNode.hasAttribute("EXTERNAL-CONVERSATION"))
			) {
				const targetElement = dropElement.parentNode as SVGGElement;
				return targetElement;
			}
			return null;
		};

		const onDragStart = function (this: SVGCircleElement, event: DragEvent) {
			const connectorElement = d3.select("#" + d3.select(this).attr("connector-id"));
			connectorElement.raise().style("pointer-events", "none");
			d3.select(this).raise().style("pointer-events", "none");
			self.svgStateHandler.dragStart();
		};

		const onDrag = function (this: SVGCircleElement, event: any) {
			// Moving the drag pointer around
			d3.select(this).attr("cx", event.x).attr("cy", event.y);

			// Keeping the line with the drag pointent
			const connectorElement = d3.select("#" + d3.select(this).attr("connector-id"));
			const node = self.conversationStore.nodes[connectorElement.attr("source-id")];
			const nodeElementConfig = nodeConfig[node.type];
			connectorElement.attr(
				"d",
				connectorPathGenerator({
					source: [node.coords.x + nodeElementConfig.width / 2, node.coords.y + nodeElementConfig.height],
					target: [event.x, event.y]
				}) as string
			);

			// Highlighing the target node
			self.svgGroup.selectAll(".cb-node--dragging").classed("cb-node--dragging", false);
			const targetElement = getTargateNode(event);
			if (targetElement) {
				targetElement.classList.add("cb-node--dragging");
			}

			// Letting SVG handler know about dragging state
			self.svgStateHandler.dragging();
		};

		const onDragEnd = function (this: SVGCircleElement, event: any) {
			const connectorElement = d3.select("#" + d3.select(this).attr("connector-id"));
			const node = self.conversationStore.nodes[connectorElement.attr("source-id")];
			const targetElement = getTargateNode(event);
			if (targetElement) {
				const targetNodeData = self.conversationStore.nodes[targetElement.id];

				const connectorId = this.getAttribute("connector-id")!;

				// setting the connector data
				self.addNodeConnector(node.id, targetNodeData.id, connectorId);
			}
			connectorElement.raise().style("pointer-events", null);
			d3.select(this).raise().style("pointer-events", null);
			self.svgGroup.selectAll(".cb-node--dragging").classed("cb-node--dragging", false);
			self.svgStateHandler.dragEnd();
		};

		this.connectorDragBehaviour = d3
			.drag<SVGCircleElement, any, any>()
			.on("start", onDragStart as any)
			.on("drag", onDrag as any)
			.on("end", onDragEnd as any);
	}

	private setAreaSelection() {
		let rect: d3.Selection<SVGRectElement, any, any, any>;
		let startX: number;
		let startY: number;
		this.svg.call(
			d3
				.drag()
				.filter((event) => isControlKey(event))
				.on("start", (event: d3.D3DragEvent<any, any, any>) => {
					rect = this.svgGroup.append("rect");
					startX = event.x - this.groundZoomTransform.x;
					startY = event.y - this.groundZoomTransform.y;
					rect.attr("x", startX).attr("y", startY).attr("class", "cb-selector");
					rect.attr("transform", `scale(${1 / this.groundZoomTransform.k})`);
				})
				.on("drag", (event) => {
					const { x, y } = event.sourceEvent;
					if (x <= this.groundConfig.minX) {
						this.groundZoomEventForced = true;
						this.groundZoomBehaviour.translateBy(this.svg as any, 50, 0);
					}
					if (x >= this.groundConfig.maxX) {
						this.groundZoomEventForced = true;
						this.groundZoomBehaviour.translateBy(this.svg as any, -50, 0);
					}
					if (y <= this.groundConfig.minY) {
						this.groundZoomEventForced = true;
						this.groundZoomBehaviour.translateBy(this.svg as any, 0, 50);
					}
					if (y >= this.groundConfig.maxY) {
						this.groundZoomEventForced = true;
						this.groundZoomBehaviour.translateBy(this.svg as any, 0, -50);
					}
					const width = event.x - startX - this.groundZoomTransform.x;
					const height = event.y - startY - this.groundZoomTransform.y;
					const translateX = width < 0 ? width : 0;
					const translateY = height < 0 ? height : 0;
					rect.attr("width", Math.abs(width));
					rect.attr("height", Math.abs(height));
					rect.attr(
						"transform",
						`scale(${1 / this.groundZoomTransform.k}) translate(${translateX}, ${translateY})`
					);
				})
				.on("end", () => {
					const selectionRect = rect.node()?.getBoundingClientRect();
					if (selectionRect && selectionRect.width > 0 && selectionRect.height > 0) {
						const selected: string[] = [];
						this.svgGroup
							.selectAll<SVGGElement, any>("g[START], g[DIALOG], g[INTENT], path[CONNECTOR]")
							.nodes()
							.forEach((node) => {
								const nodeRect = node.getBoundingClientRect();
								if (intersects(selectionRect, nodeRect)) {
									selected.push(node.id);
								}
							});
						this.conversationStore.setSelectNodes(selected);
					}
					rect.remove();
				}) as any
		);
	}

	public selectNode(event: MouseEvent, node: ConversationNode) {
		if (isControlandAltKey(event)) {
			this.createConnectorBetweenNodes(event, node);
		} else {
			this.conversationStore.setSelectNode({ id: node.id, isCtrlKey: isControlKey(event) });
		}
	}

	private isTouchGroundBoundry(x: number, y: number): boolean {
		return (
			x >= this.groundConfig.minX &&
			x <= this.groundConfig.maxX &&
			y >= this.groundConfig.minY &&
			y <= this.groundConfig.maxY
		);
	}

	private getTouchedCoordinates(x: number, y: number, nodeType: ConversationNodeType) {
		const config = nodeConfig[nodeType];

		const halfNodeWidth = (config.width / 2) * this.groundZoomTransform.k;
		const halfNodeHeight = (config.height / 2) * this.groundZoomTransform.k;

		x = x - this.groundConfig.minX - halfNodeWidth - this.groundZoomTransform.x;
		y = y - this.groundConfig.minY - halfNodeHeight - this.groundZoomTransform.y;

		const svgX = x / this.groundZoomTransform.k;
		const svgY = y / this.groundZoomTransform.k;

		return { x: this.positiveNumber(svgX), y: this.positiveNumber(svgY) };
	}

	public zoomIn() {
		this.groundZoomEventForced = true;
		this.groundZoomBehaviour.scaleBy(this.svg as any, 2);
	}
	public zoomOut() {
		this.groundZoomEventForced = true;
		this.groundZoomBehaviour.scaleBy(this.svg as any, 0.5);
	}
	public resetZoom() {
		this.groundZoomEventForced = true;
		this.transform(0, 0, 1);
	}

	private transform(x: number, y: number, k: number) {
		this.groundZoomTransform = d3.zoomIdentity.translate(x, y).scale(k);
		this.svg.call(this.groundZoomBehaviour.transform as any, this.groundZoomTransform);
		this.svgGroup.attr("transform", this.groundZoomTransform as any);
	}
	private translateBy(x: number, y: number) {
		this.groundZoomEventForced = true;
		this.groundZoomBehaviour.translateBy(this.svg as any, x, y);
	}

	private generateNodeId(): string {
		return "_" + v4();
	}

	private createConnector(event: any, x: number, y: number) {
		setTimeout(() => {
			const targetElement = document.elementFromPoint(
				event.sourceEvent.clientX - 10,
				event.sourceEvent.clientY
			)! as SVGElement;
			const parentNode = targetElement?.parentNode! as SVGGElement;
			const self = this;
			if (
				targetElement &&
				(parentNode.hasAttribute("DIALOG") ||
					parentNode.hasAttribute("INTENT") ||
					parentNode.hasAttribute("START") ||
					parentNode.hasAttribute("EXTERNAL-CONVERSATION"))
			) {
				const nodeElement = d3.select<SVGGElement, any>(parentNode as any);
				const sourceNodeId = nodeElement.attr("id");

				// Connector Pre Validation
				const message = this.connectorPreValidation(sourceNodeId);

				if (message) {
					this.$message.warning(message);
					return null;
				}

				const nodeId = this.generateNodeId();
				this.addNode<Connector>({
					id: nodeId,
					key: 0,
					type: ConversationNodeType.Connector,
					data: { sourceId: sourceNodeId },
					coords: { x, y }
				});
			}
		}, 10);
	}

	private createConnectorBetweenNodes(event: any, node: ConversationNode) {
		setTimeout(() => {
			const targetClientX = event.clientX;
			const targetClientY = event.clientY;
			const targetNodeId = node.id;
			let nodeToConnectId: string | undefined;
			const selectedNodes = Object.keys(this.conversationStore.selectedNodes);

			if (selectedNodes.length === 1) {
				nodeToConnectId = selectedNodes.find(
					(nodeId) =>
						this.conversationStore.nodes[nodeId] &&
						this.conversationStore.nodes[nodeId].type !== ConversationNodeType.Connector
				);

				const sourceNodeId = nodeToConnectId;
				if (sourceNodeId) {
					const node = this.conversationStore.nodes[sourceNodeId];
					if (
						node.type === ConversationNodeType.Dialog ||
						node.type === ConversationNodeType.ExternalConversation ||
						node.type === ConversationNodeType.Path ||
						node.type === ConversationNodeType.Conversation
					) {
						// Connector Pre Validation
						const message = this.connectorPreValidation(sourceNodeId);
						if (message) {
							this.$message.warning(message);
							return null;
						} else {
							// Check if there's already a dangling connector node present with the source node.
							const sourceConnectors = this.getConnectors(sourceNodeId);
							const anyTarget = sourceConnectors.find((f) => f.data.targetId);
							if (sourceConnectors.length === 1 && !anyTarget) {
								// delete the existing connector
								this.conversationStore.removeNode(sourceConnectors[0].id);
							}

							// Connector Post Validation
							const message = this.connectorPostValidation(sourceNodeId, targetNodeId);
							if (message) {
								this.$message.warning(message);
								return null;
							} else {
								const nodeId = this.generateNodeId();
								this.addNode<Connector>({
									id: nodeId,
									key: 0,
									type: ConversationNodeType.Connector,
									data: { sourceId: sourceNodeId },
									coords: { x: targetClientX, y: targetClientY }
								});
								this.addNodeConnectorBetweenNodes(sourceNodeId, targetNodeId, nodeId);
							}
						}
					}
				}
			}
		}, 10);
	}

	private createPath(x: number, y: number): void {
		const nodeId = this.generateNodeId();
		this.addNode<Path>({
			id: nodeId,
			key: 0,
			type: ConversationNodeType.Path,
			data: {
				name: "Path",
				isFallback: false,
				imageUrl: null,
				pageId: null,
				pageMetaData: null,
				contentId: null,
				order: 0,
				entityValueIds: [],
				intents: [],
				interactionStyleId: PathDefaultInteractionStyle
			},
			coords: { x, y }
		});
	}
	private createDialog(event: any, x: number, y: number, isHead?: boolean) {
		const nodeId = this.generateNodeId();
		this.addNode<Dialog>({
			id: nodeId,
			key: 0,
			type: isHead ? ConversationNodeType.Conversation : ConversationNodeType.Dialog,
			data: {
				name: isHead ? "Start" : "Dialog",
				retryCount: 3,
				text: "",
				order: 0,
				imageUrl: null,
				pageId: null,
				pageMetaData: null,
				contentId: null,
				voiceUrl: null,
				isHead,
				silentDialogText: null,
				fallbackDialogText: null,
				interactionStyleId: isHead ? StartDialogDefaultInteractionStyle : DialogDefaultInteractionStyle,
				isConclusion: DefaultIsConclusionDialogVal
			},
			coords: { x, y }
		});
	}

	private createExternalConversation(event: any, x: number, y: number) {
		const nodeId = this.generateNodeId();
		this.addNode<Conversation>({
			id: nodeId,
			key: 0,
			type: ConversationNodeType.ExternalConversation,
			data: {
				name: "Ext. Conversation",
				description: "",
				imageUrl: null,
				pageId: null,
				pageMetaData: null,
				contentId: null
			},
			coords: { x, y }
		});
	}

	private positiveNumber(value: number): number {
		if (Math.sign(value) === -1) {
			return 0;
		}
		return value;
	}

	private keyboardEventInitialize(): void {
		this.builderSelection.on("keydown", this.onKeydownBuilder);
		window.addEventListener("keydown", this.onKeydownWindow);
	}
	private keyboardEventDestroy(): void {
		this.builderSelection.on("keydown", null);
		window.removeEventListener("keydown", this.onKeydownWindow);
	}

	private onKeydownBuilder(event: KeyboardEvent) {
		if (isControlKey(event) && event.key === "c") {
			const selectedConversationNodes: NodesClipboard = {
				nodes: {},
				intents: { ids: [], directory: {} },
				entities: { ids: [], directory: {} }
			};
			const selectedNodeIds = Object.keys(this.conversationStore.selectedNodes).filter((id) => {
				// Filter out the orphen connectors which don't have a source node to connect with
				const node = this.conversationStore.nodes[id];
				if (
					node.type === ConversationNodeType.Connector &&
					!this.conversationStore.selectedNodes[(node.data as Connector).sourceId]
				) {
					return false;
				}
				return true;
			});
			const yCoordinates = selectedNodeIds.map<number>((id) => this.conversationStore.nodes[id].coords.y);
			const topFirstNodeY = Math.min(...yCoordinates);
			selectedNodeIds.forEach((id) => {
				const node = cloneDeep(this.conversationStore.nodes[id]);
				node.coords.y = node.coords.y - topFirstNodeY;
				selectedConversationNodes.nodes[id] = node;

				// Set connector target id undefined, if target node isn't selected.
				if (node.type === ConversationNodeType.Connector) {
					const connectorData = node.data as Connector;
					if (connectorData.targetId && !this.conversationStore.selectedNodes[connectorData.targetId]) {
						connectorData.targetId = undefined;
					}
				}

				if (node.type === ConversationNodeType.Path) {
					const pathData = node.data as Path;
					const usedIntents = pathData.intents
						.filter(
							(i) =>
								this.conversationStore.intents.directory[i.id] &&
								!selectedConversationNodes.intents.ids.includes(i.id)
						)
						.map((i) => this.conversationStore.intents.directory[i.id]);

					const usedEntities = usedIntents
						.filter(
							(i) =>
								i.entityId &&
								this.conversationStore.entities.directory[i.entityId] &&
								!selectedConversationNodes.entities.ids.includes(i.entityId)
						)
						.map((i) => this.conversationStore.entities.directory[i.entityId!]);

					// Copy all used entities to clipboard.
					usedEntities.forEach((entity) => {
						selectedConversationNodes.entities.ids.push(entity.id);
						selectedConversationNodes.entities.directory[entity.id] = entity;
					});

					// Copy all used entities to clipboard.
					usedIntents.forEach((intent) => {
						selectedConversationNodes.intents.ids.push(intent.id);
						selectedConversationNodes.intents.directory[intent.id] = intent;
					});
				}
			});
			copyToClipboard(JSON.stringify(selectedConversationNodes));
			this.builderSelection.node()?.focus();
		}
		if (isControlKey(event) && event.key === "v") {
			readFromClipboard().then((text) => {
				try {
					const decoded = JSON.parse(text) as IMetaData;
					if (decoded.nodes) {
						const svgSize = this.getSVGSize();
						const pageType = this.$route.params.pagetype as BuilderPageType;
						const id = this.$route.params.id;
						this.conversationStore
							.pasteMetaData({ metaData: decoded, svgSize, pageType, id })
							.then((result) => {
								if (result.err) {
									this.$message.error(result.err);
									return;
								}
								const { nodes, entities, intents } = this.conversationStore;
								const { intentCount } = countObjects(nodes, entities, intents);
								const { intent: maxIntents, dialog: maxDialogs } = EntityLengths.conversation.child;

								if (intentCount >= maxIntents) {
									this.$message.warning(
										fmtMsg(ConversationBuilderLocale.ConversationMaxNodeCountWarning, {
											node: "Path",
											maxIntents
										})
									);
								}
							});
					}
				} catch (err) {
					return;
				}
			});
		}
		const translateUnit = 100;
		switch (event.key) {
			case "Backspace":
			case "Delete":
				this.conversationStore.removeNodes(Object.keys(this.conversationStore.selectedNodes));
				break;
			case "ArrowDown":
				this.translateBy(0, -translateUnit);
				break;
			case "ArrowUp":
				this.translateBy(0, translateUnit);
				break;
			case "ArrowLeft":
				this.translateBy(translateUnit, 0);
				break;
			case "ArrowRight":
				this.translateBy(-translateUnit, 0);
				break;
		}
	}

	private onKeydownWindow(event: KeyboardEvent) {
		if (isControlKey(event)) {
			switch (event.key) {
				case "+":
				case "=":
					this.zoomIn();
					event.preventDefault();
					break;
				case "-":
					this.zoomOut();
					event.preventDefault();
					break;
				case "0":
					this.resetZoom();
					event.preventDefault();
					break;
			}
		}
	}

	public getSVGSize(): [number, number] {
		const svgRect = this.svgGroup.node()?.getBBox();
		const width = svgRect?.width!;
		const height = svgRect?.height!;
		return [width, height];
	}
}

interface NodesClipboard {
	nodes: INodes;
	intents: EntityDataSet<Intent>;
	entities: EntityDataSet<Entity>;
}
